import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ICreateFeedbackProps,
  IFeedbacksProps,
  IUpdateFeedbackProps,
} from '../dtos/IFeedbacksProps';
import api from '../services/api';
import { getDeviceType } from '../utils/getDataFromAccess';
import { ILocationData } from '../dtos/ILocationsProps';

interface FeedbackContextData {
  feedbacks: IFeedbacksProps;
  handleFeedback: (feedback: ICreateFeedbackProps) => void;
  updateFeedback: (feedback: IUpdateFeedbackProps) => void;
  openFeedback: boolean;
  setOpenFeedback: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UserLocationProps {
  city: string;
  state: string;
  country: string;
}

const FeedbackContext = createContext<FeedbackContextData>(
  {} as FeedbackContextData,
);

const FeedbackProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [feedbacks, setFeedback] = useState<IFeedbacksProps>(
    {} as IFeedbacksProps,
  );
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string>('');
  const [userLocation, setUserLocation] = useState<
    UserLocationProps | undefined
  >();
  const startTimeRef = useRef(Date.now());
  // Guard para evitar chamadas duplicadas por activity_id
  const feedbackRequestRef = useRef<string | null>(null);
  const userLocationRef = useRef(userLocation);

  // Manter ref sincronizado com state
  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  const handleEngagementTime = useCallback((): number => {
    return Math.round((Date.now() - startTimeRef.current) / 1000);
  }, []);

  const createFeedback = useCallback(
    async (data: ICreateFeedbackProps): Promise<void> => {
      const deviceType = getDeviceType();
      const engagementTime = handleEngagementTime();
      const location = userLocationRef.current;

      const updateData = {
        ...data,
        engagement_time: engagementTime,
        device_type: deviceType,
        user_location: location
          ? `${location.country}, ${location.state}, ${location.city}`
          : undefined,
      };

      try {
        const response = await api.post<IFeedbacksProps>(
          '/feedbacks',
          updateData,
        );
        setFeedback(response.data);
        setFeedbackId(response.data.id);
      } catch {
        // Falha silenciosa
      }
    },
    [handleEngagementTime],
  );

  const handleFeedback = useCallback(
    async (data: ICreateFeedbackProps): Promise<void> => {
      // Evitar requisição duplicada para a mesma atividade
      if (feedbackRequestRef.current === data.activity_id) return;
      feedbackRequestRef.current = data.activity_id;

      try {
        const response = await api.get(`/feedbacks/${data.activity_id}`);

        if (response.status === 204) {
          await createFeedback(data);
        } else if (response.status === 200) {
          setFeedback(response.data);
          setFeedbackId(response.data.id);
        }
      } catch {
        // Permitir retry ao trocar de atividade
        feedbackRequestRef.current = null;
      }
    },
    [createFeedback],
  );

  const updateFeedback = useCallback(
    async (data: IUpdateFeedbackProps): Promise<void> => {
      if (!feedbackId) {
        return;
      }

      const handleEngagement = handleEngagementTime();

      const updateData = {
        ...data,
        engagement_time: handleEngagement,
      };

      try {
        const response = await api.patch(
          `/feedbacks/${feedbackId}`,
          updateData,
        );
        setFeedback(response.data);
      } catch {
        // Falha silenciosa
      }
    },
    [feedbackId, handleEngagementTime],
  );

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async position => {
        const { latitude, longitude } = position.coords;

        try {
          const locationData = await fetchLocationData(latitude, longitude);
          setUserLocation(locationData);
        } catch {
          // Falha silenciosa — localização não é crítica
        }
      });
    } else {
      setUserLocation(undefined);
    }
  }, []);

  const fetchLocationData = async (
    latitude: number,
    longitude: number,
  ): Promise<UserLocationProps> => {
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/reverse?key=${process.env.REACT_APP_LOCATIONIQ_API_KEY}&lat=${latitude}&lon=${longitude}&format=json&`,
      );

      const result = (await response.json()) as ILocationData;

      if (result) {
        return {
          city: result.address.city || 'Unknown',
          state: result.address.state || 'Unknown',
          country: result.address.country || 'Unknown',
        };
      }

      return { city: 'Unknown', state: 'Unknown', country: 'Unknown' };
    } catch (error) {
      throw new Error(`Error fetching location data: `);
    }
  };

  return (
    <FeedbackContext.Provider
      value={{
        openFeedback,
        setOpenFeedback,
        feedbacks,
        handleFeedback,
        updateFeedback,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
};

function useFeedback(): FeedbackContextData {
  const context = React.useContext(FeedbackContext);

  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }

  return context;
}

export { FeedbackProvider, useFeedback };
